import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useFieldArray, useForm, Controller } from "react-hook-form";
import { Form } from "components/core/Form";
import {
	TextField,
	EmailField,
	PhoneField,
} from "components/core/FormFields";
import { Button } from "components/core/Button";
import { Paragraph } from "components/core/Typography";
import { Link } from "components/core/Link";
import { AdditionalNameInputsGroupV2 } from "components/facility/UnitReservationFormV2/AdditionalNameInputsGroupV2";
import { AdditionalNamesV2 } from "components/facility/UnitReservationFormV2/AdditionalNamesV2";
import { WarningIcon } from "components/core/Icons";
import { CalendarV2 } from "components/facility/UnitReservationFormV2/calendarV2";
import {
	ADDITIONAL_NAMES_FIELD_ARRAY_NAME,
	ADDITIONAL_NAME_INPUTS,
	UNIT_FORM_INPUTS
} from "components/facility/UnitReservationFormV2/UnitReservationFormInputsV2";
import "./UnitReservationFormV2.scss";
import { trackCTAClick } from "hooks/tracking/utils/clickTrackingUtils";

const UnitReservationFormV2 = ({
	onSubmit,
	reservationWindow,
	closures,
	cityUrl,
	errorReservationMessages,
	setErrorReservationMessages,
	handleFetchNumber
}) => {
	const useFormMethods = useForm({
		mode: "all",
		shouldUnregister: true,
		shouldUseNativeValidation: false,
		defaultValues: {
			firstName: "",
			lastName: "",
			email: "",
			phoneNumber: "",
			moveInDate: ""
		}
	});

	// we have to pass the whole methods object to the form,
	// but we also need to get control out for the field array
	const {
		control,
		getValues,
		watch,
		formState: { isSubmitted, isValid }
	} = useFormMethods;
	const [lastSubmitValues, setLastSubmitValues] = useState({});
	const [wasSubmittedWithoutChanges, setIsSubmitWithoutChanges] =
		useState(false);
	const [phone, setPhone] = useState({});
	const hasPhoneNumber = Boolean(phone?.number && phone?.formattedNumber);

	const { fields, append, remove } = useFieldArray({
		control,
		name: ADDITIONAL_NAMES_FIELD_ARRAY_NAME,
		shouldUnregister: true
	});



	const haveValuesChanged = () => {
		const currentValues = getValues();
		return Object.keys(currentValues).some(
			(key) => currentValues[key] !== lastSubmitValues[key]
		);
	};

	const getLocalNumber = async () => {
		if (!cityUrl) return;
		const [, country, city] = cityUrl.replace(/^\//, "").split("/");
		if (!city || !country) return;
		try {
			const response = await handleFetchNumber(city, country);
			setPhone(response);
		} catch (error) {
			setPhone({});
			console.error("Error fetching phone number", error); // eslint-disable-line no-console
		}
	};

	const handleSubmit = (data, event) => {
		event.preventDefault();

		if (isSubmitted && !haveValuesChanged()) {
			getLocalNumber();
			setIsSubmitWithoutChanges(true);
			setErrorReservationMessages("ReSubmissionError");
			return;
		}

		setLastSubmitValues(getValues());
		setIsSubmitWithoutChanges(false);
		
		const clickUrl = data?.unit?.url || null;

		trackCTAClick(
			'complete-reservation',
			'Reserve Now Button Clicked',
			'Reserve Now',
			clickUrl,
		);

		onSubmit(data);
	};

	useEffect(() => {
		if (isSubmitted) {
			// Watch for changes in form fields
			const subscription = watch(() => {
				if (wasSubmittedWithoutChanges) {
					setIsSubmitWithoutChanges(false);
					setErrorReservationMessages();
				}
			});
			// clean up subscription on unmount
			return () => subscription.unsubscribe();
		}
		return () => {};
	}, [watch, wasSubmittedWithoutChanges, isSubmitted, setErrorReservationMessages]);

	return (
		<div className="unit-reservation-form">
			<Form
				methods={useFormMethods}
				onSubmit={handleSubmit}
				formName="unit-reservation-form"
				className="unit-reservation-form-container"
			>
				<div className="reservation-fields-container">
					<div className="main-inputs">
						<TextField {...UNIT_FORM_INPUTS.firstName} />
						<TextField {...UNIT_FORM_INPUTS.lastName} />
						<EmailField {...UNIT_FORM_INPUTS.email} />
						<div className="phone-input-container">
							<PhoneField {...UNIT_FORM_INPUTS.phoneNumber} />
							<span className="phone-helper-text">
								So that we can validate your reservation
							</span>
						</div>
						


					</div>
					<AdditionalNamesV2
						handleAddName={() => {
							append({}, { shouldFocus: false });
						}}
					>
						{fields.map((field, index) => (
							<AdditionalNameInputsGroupV2
								key={field.id}
								handleRemoveName={() => remove(index)}
							>
								<TextField
									{...ADDITIONAL_NAME_INPUTS.additionalFirstName(
										index
									)}
								/>
								<TextField
									{...ADDITIONAL_NAME_INPUTS.additionalLastName(
										index
									)}
								/>
							</AdditionalNameInputsGroupV2>
						))}
					</AdditionalNamesV2>
				</div>
				<Controller
						name="moveInDate"
						control={control}
						rules={{
							required: "Please select a move-in date"
						}}
						render={({ field }) => (
							<CalendarV2
								input={field}
								closures={closures}
								reservationWindow={reservationWindow}
								moveInDate={field.value}
							/>
						)}
				/>
				{wasSubmittedWithoutChanges && (
					<div className="main-alert-container">
						<WarningIcon size="xlarge" />
						<div>
							<Paragraph
								weight="extraBold"
								last
							>
								{"Your reservation had an error. "}
							</Paragraph>
							<Paragraph last>
								Please check the information you provided and
								make a change to resubmit your reservation.
								{errorReservationMessages !==
									"ReSubmissionError" && (
									<>
										<br /> &nbsp;&#8226;{" "}
										{errorReservationMessages}{" "}
									</>
								)}
							</Paragraph>
						</div>
					</div>
				)}

				<div className="checkout-reserve-button-container">
					<Button
						type="submit"
						theme="primary"
						className="checkout-reserve-button"
						disabled={!isValid || wasSubmittedWithoutChanges}
					>
						Reserve For Free
					</Button>
					<div className="resrvation-fee-guide">
						<p className="reservation-fee-guide-text">
							You will not be charged for a reservation until move-in
						</p>
						<p className="reservation-terms">
							No obligation to rent. By reserving this unit, you agree
							to our {" "}
							<a 
								className="disclaimer-link" 
								href="/about-us/terms-and-conditions/" 
								target="_blank" 
								rel="noopener noreferrer"
							>
								Terms &amp; Services
							</a> and our {" "}
							<a 
								className="disclaimer-link" 
								href="/about-us/privacy" 
								target="_blank" 
								rel="noopener noreferrer"
							>
								Privacy Policy
							</a>
						</p>
					</div>
				</div>
				{wasSubmittedWithoutChanges && (
					<div className="main-alert-container alert-with-suggestion">
						<Paragraph last>
							If you continue to experience errors with this
							reservation, you may need to come back and try again
							at a later time or &nbsp;
							<Link
								href={cityUrl}
								rel="noopener noreferrer"
							>
								try exploring other storage options in the same
								area if you are looking to reserve storage right
								now
							</Link>
							{hasPhoneNumber && (
								<span>
									{" "}or call{" "}
									<a href={`tel:${phone.number}`}>
										{phone.formattedNumber}
									</a>{" "}
									to get help in finding the best storage option
									for you.
								</span>
							)}
						</Paragraph>
					</div>
				)}
			</Form>
		</div>
	);
};

UnitReservationFormV2.propTypes = {
	onSubmit: PropTypes.func.isRequired,
	reservationWindow: PropTypes.number.isRequired,
	closures: PropTypes.arrayOf(PropTypes.string),
	unitPrice: PropTypes.number,
	cityUrl: PropTypes.string,
	errorReservationMessages: PropTypes.string,
	setErrorReservationMessages: PropTypes.func,
	handleFetchNumber: PropTypes.func
};

export default UnitReservationFormV2;
